import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { hardRefreshToLatest } from "@/lib/app-version";

export const CHUNK_RETRY_FLAG = "nk_chunk_retry_reload_at";

/**
 * React.lazy that survives deploys.
 *
 * When a new deploy replaces the hashed chunk filenames this running build
 * points at, a plain lazy() import rejects (the old chunk 404s) and the user
 * gets a dead screen. This wrapper hard-refreshes ONCE (sessionStorage-guarded,
 * 60s) — unregister SW + clear Cache API + cache-bust navigation — so a plain
 * reload that re-serves a stale precached shell can't leave the user stuck.
 * A second consecutive failure rethrows into the ErrorBoundary.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
    importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
    return lazy(async () => {
        try {
            return await importer();
        } catch (err) {
            let reloadedRecently = false;
            try {
                const last = Number(sessionStorage.getItem(CHUNK_RETRY_FLAG) || "0");
                reloadedRecently = Date.now() - last < 60_000;
                if (!reloadedRecently) {
                    sessionStorage.setItem(CHUNK_RETRY_FLAG, String(Date.now()));
                }
            } catch {
                /* sessionStorage unavailable — still hard-refresh */
            }
            if (!reloadedRecently) {
                console.warn("[lazy-retry] chunk load failed — hard-refresh onto latest build", err);
                void hardRefreshToLatest();
                return new Promise<never>(() => undefined);
            }
            throw err;
        }
    });
}
